import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { SisBundleSummary } from "@canvas-sync/shared";
import { SisBundleStatusBadge } from "./SisBundleStatusBadge";
import { formatDropId, formatRelative, groupByDate } from "../lib/format";
import { canvasOutcomeKind } from "../lib/canvasState";

/**
 * SIS bundle history rendered as a collapsible timeline of date sections.
 * Mirrors `RunsTimeline` / `UploadsTimeline` so the three list pages stay
 * visually consistent: shared `<thead>`, one `<tbody>` per date bucket,
 * click-to-collapse header row, and the same caret + title + sublabel +
 * count layout.
 *
 * Bucketing is by `createdAt` — the n8n drop timestamp baked into the
 * dropId. Each row's "Created" column shows the relative time so the
 * user can see at a glance how stale a bundle is without doing math.
 */
export function SisBundlesTimeline({
  bundles,
}: {
  bundles: SisBundleSummary[];
}) {
  const groups = useMemo(
    () => groupByDate(bundles, (b) => b.createdAt),
    [bundles],
  );
  // Default: only the newest (first) group expanded — older days collapsed.
  // Lazy initializer computes from the initial `bundles` prop to avoid a
  // flash of all-expanded on first paint. User toggles persist for the visit.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const initialGroups = groupByDate(bundles, (b) => b.createdAt);
    return new Set(initialGroups.slice(1).map((g) => g.key));
  });

  function toggle(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <table className="runs-table runs-table-grouped">
      <thead>
        <tr>
          <th>Bundle</th>
          <th>Status</th>
          <th>Triggered by</th>
          <th>Canvas</th>
          <th>Fix</th>
          <th>Canvas import ID</th>
          <th>Created</th>
        </tr>
      </thead>
      {groups.map((g) => {
        const isCollapsed = collapsed.has(g.key);
        return (
          <tbody key={g.key} className="runs-group">
            <tr className="runs-group-head">
              <td colSpan={7}>
                <button
                  type="button"
                  className="runs-group-toggle"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggle(g.key)}
                >
                  <span className="runs-group-caret" aria-hidden="true" />
                  <span className="runs-group-title">{g.label}</span>
                  {g.sublabel && (
                    <span className="runs-group-sublabel">{g.sublabel}</span>
                  )}
                  <span className="runs-group-count">
                    {g.items.length} bundle{g.items.length === 1 ? "" : "s"}
                  </span>
                </button>
              </td>
            </tr>
            {!isCollapsed &&
              g.items.map((b) => (
                <tr key={b.bundleId}>
                  <td>
                    <Link
                      to={`/sis-bundles/${encodeURIComponent(b.bundleId)}`}
                      className="run-link"
                      title={b.bundleId}
                    >
                      {formatDropId(b.dropId)}
                    </Link>
                    {b.dryRun && (
                      <span
                        className="badge badge-cancelled"
                        style={{ marginLeft: 8, fontSize: 10 }}
                      >
                        dry-run
                      </span>
                    )}
                    {b.submissionMode === "GHOST_FIX_ONLY" && (
                      <span
                        className="badge badge-queued"
                        style={{ marginLeft: 8, fontSize: 10 }}
                        title="This bundle sends only the ghost-fix CSV — the 5 SIS files are not sent"
                      >
                        ghost fix only
                      </span>
                    )}
                    {b.missingFiles && (
                      <span
                        className="badge badge-warn"
                        style={{ marginLeft: 8, fontSize: 10 }}
                        title={`Missing from this drop: ${b.missingFiles.join(", ")}`}
                      >
                        missing {b.missingFiles.length}{" "}
                        {b.missingFiles.length === 1 ? "file" : "files"}
                      </span>
                    )}
                  </td>
                  <td>
                    <SisBundleStatusBadge status={b.status} />
                    <CanvasOutcome b={b} />
                  </td>
                  <td>
                    <span className="trigger-pill">
                      {b.triggeredBy === "manual-retrigger"
                        ? "Manual re-trigger"
                        : "Scheduled (n8n)"}
                    </span>
                  </td>
                  <td>
                    <span className={`env-tag env-tag-${b.env}`}>{b.env}</span>
                  </td>
                  <td>
                    {b.fixIncluded ? (
                      <span className="muted">included</span>
                    ) : b.fixSkipReason ? (
                      <span className="muted" title={b.fixSkipReason}>
                        skipped
                        {b.contradictionCount !== undefined &&
                          b.contradictionCount > 0 && (
                            <> ({b.contradictionCount})</>
                          )}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="mono">{b.sisImportId ?? "—"}</td>
                  <td className="muted">{formatRelative(b.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        );
      })}
    </table>
  );
}

/**
 * Canvas processing outcome for a posted bundle, shown under the lifecycle
 * status badge so a Canvas-side failure isn't hidden behind "Submitted".
 * Renders nothing for bundles that never POSTed — their lifecycle status is the
 * whole story.
 */
function CanvasOutcome({ b }: { b: SisBundleSummary }) {
  if (
    b.status !== "SUBMITTED" &&
    b.status !== "SUBMITTED_SIS_ONLY" &&
    b.status !== "SUBMITTED_FIX_ONLY"
  )
    return null;
  const kind = canvasOutcomeKind(b.canvasWorkflowState);
  const w = b.canvasWarnings ?? 0;
  const e = b.canvasErrors ?? 0;

  let text: string;
  switch (kind) {
    case "in-flight":
      text = "Canvas: importing…";
      break;
    case "ok":
      text = "Canvas: imported";
      break;
    case "warn":
      text = `Canvas: imported${w ? ` · ${w} warning${w === 1 ? "" : "s"}` : ""}`;
      break;
    case "bad":
      text = `Canvas: ${
        (b.canvasWorkflowState ?? "").toLowerCase() === "aborted"
          ? "aborted"
          : "FAILED"
      }${e ? ` · ${e} error${e === 1 ? "" : "s"}` : ""}`;
      break;
    default:
      text = "Canvas: awaiting…"; // posted, Canvas state not captured yet
  }

  return (
    <div
      className="muted small"
      style={
        kind === "bad" ? { color: "var(--danger)", fontWeight: 600 } : undefined
      }
    >
      {text}
    </div>
  );
}
